/** Mirrors server rules in altcase-api orderService (confirmPayment, cancelOrder, updateOrderUTR). */

export const ORDER_STATUS = {
  ORDER_PLACED: 'ORDER_PLACED',
  PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
  PARTIALLY_TRANSFERRED: 'PARTIALLY_TRANSFERRED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  OFFER_EXPIRED: 'OFFER_EXPIRED',
  SHARES_TRANSFERRED: 'SHARES_TRANSFERRED',
};

const ALTcase_ASSIGN_STATUSES = new Set([
  ORDER_STATUS.PAYMENT_RECEIVED,
  ORDER_STATUS.PARTIALLY_TRANSFERRED,
]);

export function canConfirmPayment(statusOrRow) {
  const status =
    typeof statusOrRow === 'object' ? statusOrRow?.status : statusOrRow;
  if (status === ORDER_STATUS.ORDER_PLACED) return true;
  const row = typeof statusOrRow === 'object' ? statusOrRow : null;
  if (
    row?.payment_pending === true &&
    resolveTransferBeforePayment(row) &&
    (status === ORDER_STATUS.SHARES_TRANSFERRED ||
      status === ORDER_STATUS.PARTIALLY_TRANSFERRED)
  ) {
    return true;
  }
  return false;
}

export function isTerminalOrderStatus(status) {
  return (
    status === ORDER_STATUS.CANCELLED || status === ORDER_STATUS.OFFER_EXPIRED
  );
}

export function canEditOrderQuantity(status) {
  return status === ORDER_STATUS.ORDER_PLACED;
}

export function canEditOrderPartnerPrice(status) {
  return !isTerminalOrderStatus(status);
}

export function canShowEditOrderAction(status, canEditQuantity, canEditPartnerPrice) {
  if (isTerminalOrderStatus(status)) return false;
  return (
    (canEditQuantity && canEditOrderQuantity(status)) ||
    (canEditPartnerPrice && canEditOrderPartnerPrice(status))
  );
}

export function canUpdateUtr(status) {
  return status !== ORDER_STATUS.COMPLETED && status !== ORDER_STATUS.CANCELLED;
}

export function canCancelOrder(status) {
  return status !== ORDER_STATUS.CANCELLED;
}

/** Fulfilled states whose revenue is counted in dashboard / leadership reports. */
export function isFulfilledOrderStatusForReports(status) {
  return (
    status === ORDER_STATUS.SHARES_TRANSFERRED || status === ORDER_STATUS.COMPLETED
  );
}

export function canExcludeOrderFromReports(row) {
  if (row?.exclude_from_reports === true) return false;
  return isFulfilledOrderStatusForReports(row?.status);
}

export function isCancelRemarksRequired(status) {
  return (
    status === ORDER_STATUS.COMPLETED ||
    status === ORDER_STATUS.SHARES_TRANSFERRED
  );
}

export function canAssignAltcasePrice(status, transferBeforePayment = false) {
  if (ALTcase_ASSIGN_STATUSES.has(status)) return true;
  return transferBeforePayment === true && status === ORDER_STATUS.ORDER_PLACED;
}

export function parseOrderMetadata(metadata) {
  if (!metadata) return {};
  if (typeof metadata === 'object' && !Array.isArray(metadata)) return metadata;
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return typeof parsed === 'object' && parsed && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

export function getPendingTransferInventoryId(row) {
  const meta = parseOrderMetadata(row?.metadata);
  if (meta.pending_transfer_inventory_id) {
    return meta.pending_transfer_inventory_id;
  }
  const allocs = getPendingTransferAllocations(row);
  return allocs[0]?.inventory_id ?? null;
}

export function getPendingTransferAllocations(row) {
  const meta = parseOrderMetadata(row?.metadata);
  const allocs = meta.pending_transfer_allocations;
  return Array.isArray(allocs) ? allocs : [];
}

/** True when lots are staged/reserved but shares not yet transferred for that staging. */
export function hasPendingLotSelection(row) {
  const meta = parseOrderMetadata(row?.metadata);
  const allocs = getPendingTransferAllocations(row);
  return meta.inventory_reserved_on_assign === true && allocs.length > 0;
}

/** Pending qty reserved on this order for a given lot (0 if none). */
export function getPendingQtyForLot(row, inventoryId) {
  if (!inventoryId) return 0;
  const alloc = getPendingTransferAllocations(row).find(
    (a) => a?.inventory_id === inventoryId
  );
  const qty = Number(alloc?.quantity);
  return Number.isFinite(qty) && qty > 0 ? qty : 0;
}

/**
 * Available qty for picker UI: API available plus this order's reserved pending qty
 * (reservation already decremented available_quantity on the server).
 */
export function getEffectiveLotAvailableQty(lot, row, resolveLotId = (l) => l?.id ?? l?.uuid) {
  const id = resolveLotId(lot);
  const available = Number(lot?.available_quantity) || 0;
  return available + getPendingQtyForLot(row, id);
}

/**
 * Keep lots with stock, plus any lots currently staged on this order
 * (fully reserved lots may have available_quantity === 0).
 */
export function mergeLotsForPicker(lots, row, resolveLotId = (l) => l?.id ?? l?.uuid) {
  const list = Array.isArray(lots) ? lots : [];
  const pendingIds = new Set(
    getPendingTransferAllocations(row).map((a) => a?.inventory_id).filter(Boolean)
  );
  return list.filter((lot) => {
    const id = resolveLotId(lot);
    const available = Number(lot?.available_quantity) || 0;
    return available > 0 || pendingIds.has(id);
  });
}

export function getRemainingOrderQty(row) {
  const qty = Number(row?.quantity);
  const fulfilled = Number(row?.fulfilled_quantity);
  if (!Number.isFinite(qty)) return 0;
  const done = Number.isFinite(fulfilled) ? fulfilled : 0;
  return Math.max(0, qty - done);
}

export function sumAllocationQty(selectedIds, qtyByLotId) {
  return selectedIds.reduce((sum, id) => sum + (Number(qtyByLotId[id]) || 0), 0);
}

/** Weighted average supplier price for selected lot allocations. */
export function computeWeightedLotPrice(selectedIds, qtyByLotId, lots, resolveLotId) {
  let weightedSum = 0;
  let totalQty = 0;
  for (const id of selectedIds) {
    const lot = lots.find((l) => resolveLotId(l) === id);
    const qty = Number(qtyByLotId[id]) || 0;
    const price = Number(lot?.supplier_price);
    if (!lot || qty <= 0 || !Number.isFinite(price)) continue;
    weightedSum += qty * price;
    totalQty += qty;
  }
  if (totalQty <= 0) return null;
  return weightedSum / totalQty;
}

/** Max qty assignable from one lot without exceeding order remaining. */
export function maxAllocatableFromLot(lotUuid, lotAvailableQty, selectedIds, qtyByLotId, targetQty) {
  const otherTotal = selectedIds
    .filter((id) => id !== lotUuid)
    .reduce((sum, id) => sum + (Number(qtyByLotId[id]) || 0), 0);
  const left = Math.max(0, targetQty - otherTotal);
  return Math.min(Number(lotAvailableQty) || 0, left);
}

export function resolveTransferBeforePayment(row) {
  return row?.transfer_before_payment === true || row?.is_trusted_partner_order === true;
}

export function hasAltcasePrice(row) {
  const n = Number(row?.altcase_price);
  return Number.isFinite(n) && n > 0;
}

export function canTransferShares(row) {
  if (!canAssignAltcasePrice(row?.status, resolveTransferBeforePayment(row))) return false;
  return hasAltcasePrice(row);
}

export function getTransferSharesDisabledHint(row) {
  if (hasAltcasePrice(row) || canTransferShares(row)) return null;
  const status = row?.status;
  if (status === ORDER_STATUS.CANCELLED || status === ORDER_STATUS.COMPLETED) return null;
  return 'Select lots first';
}
